import { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { VehicleCard } from "@/components/VehicleCard";
import { fetchVehicles } from "@/lib/api";

const CATEGORIES = ["All", "Sedans", "SUVs", "Pickup Trucks", "Commercial Vehicles"];
const PRICE_OPTIONS = [
  { label: "Any Price", value: 0 },
  { label: "Under $5,000", value: 5000 },
  { label: "Under $8,000", value: 8000 },
  { label: "Under $12,000", value: 12000 },
  { label: "Under $16,000", value: 16000 },
  { label: "Under $20,000", value: 20000 },
];
const MILEAGE_OPTIONS = [
  { label: "Any Mileage", value: 0 },
  { label: "Under 90,000 mi", value: 90000 },
  { label: "Under 120,000 mi", value: 120000 },
  { label: "Under 150,000 mi", value: 150000 },
  { label: "Under 180,000 mi", value: 180000 },
];

const selectCls =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700";

export default function Catalog() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [make, setMake] = useState("All");
  const [model, setModel] = useState("All");
  const [year, setYear] = useState("All");
  const [maxPrice, setMaxPrice] = useState(0);
  const [maxMileage, setMaxMileage] = useState(0);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchVehicles().then((d) => { setVehicles(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const makes = useMemo(() => ["All", ...Array.from(new Set(vehicles.map((v) => v.make))).sort()], [vehicles]);
  const models = useMemo(() => {
    const pool = make === "All" ? vehicles : vehicles.filter((v) => v.make === make);
    return ["All", ...Array.from(new Set(pool.map((v) => v.model))).sort()];
  }, [vehicles, make]);
  const years = useMemo(() => ["All", ...Array.from(new Set(vehicles.map((v) => v.year))).sort((a, b) => b - a)], [vehicles]);

  const filtered = useMemo(() => {
    return vehicles.filter((v) => {
      if (category !== "All" && v.category !== category) return false;
      if (make !== "All" && v.make !== make) return false;
      if (model !== "All" && v.model !== model) return false;
      if (year !== "All" && String(v.year) !== String(year)) return false;
      if (maxPrice && v.price > maxPrice) return false;
      if (maxMileage && v.mileage > maxMileage) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${v.year} ${v.make} ${v.model} ${v.stock_number}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [vehicles, category, make, model, year, maxPrice, maxMileage, search]);

  const resetFilters = () => {
    setCategory("All"); setMake("All"); setModel("All"); setYear("All");
    setMaxPrice(0); setMaxMileage(0); setSearch("");
  };

  return (
    <Layout>
      <div className="bg-slate-900" data-testid="catalog-header">
        <div className="mx-auto max-w-7xl px-4 py-12">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Vehicle Catalog</h1>
          <p className="mt-2 text-slate-300 max-w-2xl">
            Affordable and mid-range used vehicles currently available for sale. All units carry a
            clean title and are priced for everyday buyers, dealers, and small businesses. Contact
            us for current availability and transport to your location.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="catalog-categories">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`catalog-category-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                category === c
                  ? "bg-blue-800 text-white border-blue-800"
                  : "bg-white text-slate-700 border-slate-300 hover:border-blue-700"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
          <aside className="border border-slate-200 rounded-md p-5 h-fit bg-slate-50" data-testid="catalog-filters">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">Filters</h2>
              <button onClick={resetFilters} className="text-xs text-blue-800 hover:underline" data-testid="catalog-reset-filters">Reset</button>
            </div>

            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Search</label>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Make, model or stock #"
                  className={selectCls}
                  data-testid="catalog-search-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Make</label>
                <select value={make} onChange={(e) => { setMake(e.target.value); setModel("All"); }} className={selectCls} data-testid="catalog-make-select">
                  {makes.map((m) => <option key={m} value={m}>{m === "All" ? "All Makes" : m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Model</label>
                <select value={model} onChange={(e) => setModel(e.target.value)} className={selectCls} data-testid="catalog-model-select">
                  {models.map((m) => <option key={m} value={m}>{m === "All" ? "All Models" : m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Year</label>
                <select value={year} onChange={(e) => setYear(e.target.value)} className={selectCls} data-testid="catalog-year-select">
                  {years.map((y) => <option key={y} value={y}>{y === "All" ? "All Years" : y}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Max Price</label>
                <select value={maxPrice} onChange={(e) => setMaxPrice(Number(e.target.value))} className={selectCls} data-testid="catalog-price-filter">
                  {PRICE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Max Mileage</label>
                <select value={maxMileage} onChange={(e) => setMaxMileage(Number(e.target.value))} className={selectCls} data-testid="catalog-mileage-filter">
                  {MILEAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          </aside>

          <div>
            <p className="text-sm text-slate-500 mb-4" data-testid="catalog-result-count">
              {loading ? "Loading inventory…" : `${filtered.length} vehicle${filtered.length === 1 ? "" : "s"} available`}
            </p>
            {!loading && filtered.length === 0 ? (
              <div className="border border-dashed border-slate-300 rounded-md p-12 text-center text-slate-500">
                No vehicles match your filters. <button onClick={resetFilters} className="text-blue-800 hover:underline">Reset filters</button>.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="catalog-grid">
                {filtered.map((v) => <VehicleCard key={v.stock_number} v={v} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
